/**
 * Step hints: a small "½" or "1" pill between neighbouring notes of the
 * "me" part whenever the melody moves by a second, so a singer walking a
 * line can see at a glance whether the next note is a half or a whole step
 * away. Rests are looked across, tied notes count once, chords use the top
 * (or bottom) note.
 *
 * Everything is read from OpenSheetMusicDisplay's graphical model after it
 * has rendered and drawn into its own SVG group, which OSMD wipes on every
 * re-render (zoom, part visibility, resize). A MutationObserver on the
 * score container notices the wipe and redraws.
 */

const GROUP_CLASS = 'step-hints';
const SVG_NS = 'http://www.w3.org/2000/svg';
const HALF = { text: '½', color: '#d97706', label: 'half step' };
const WHOLE = { text: '1', color: '#2563eb', label: 'whole step' };
const MIN_PILL_PX = 11;

const measureRows = (osmd) => osmd?.GraphicSheet?.MeasureList || osmd?.graphic?.measureList || [];

const instrumentIdOf = (measure) => {
  const id = measure?.ParentStaff?.ParentInstrument?.Id;
  return id === undefined || id === null ? null : String(id);
};

const skipNote = (note) => (
  !note || !note.Pitch || note.IsCueNote || note.IsGraceNote || note.ParentVoiceEntry?.IsGrace
  || (note.NoteTie && note.NoteTie.StartNote !== note)
);

/**
 * The sounding notes of one instrument in written order, one per staff
 * entry: the top note of a chord, or the bottom one for voice='bottom'.
 * Returns one sequence per staff of that instrument.
 */
export function collectMelodyNotes(osmd, instrumentId, voice = 'top') {
  const wanted = String(instrumentId);
  const perStaff = new Map();
  for (const row of measureRows(osmd)) {
    if (!row) continue;
    for (const measure of row) {
      if (!measure || instrumentIdOf(measure) !== wanted) continue;
      if (!perStaff.has(measure.ParentStaff)) perStaff.set(measure.ParentStaff, []);
      const sequence = perStaff.get(measure.ParentStaff);
      for (const entry of measure.staffEntries || []) {
        let pick = null;
        for (const voiceEntry of entry.graphicalVoiceEntries || []) {
          for (const graphicalNote of voiceEntry.notes || []) {
            const note = graphicalNote.sourceNote;
            if (skipNote(note)) continue;
            const semitone = note.Pitch.getHalfTone();
            const better = voice === 'bottom' ? semitone < pick?.semitone : semitone > pick?.semitone;
            if (!pick || better) pick = { note: graphicalNote, semitone };
          }
        }
        if (pick) sequence.push(pick);
      }
    }
  }
  return [...perStaff.values()];
}

/** Consecutive note pairs a second apart: { from, to, half }. */
export function collectStepPairs(osmd, instrumentId, voice) {
  const pairs = [];
  for (const sequence of collectMelodyNotes(osmd, instrumentId, voice)) {
    for (let i = 1; i < sequence.length; i++) {
      const distance = Math.abs(sequence[i].semitone - sequence[i - 1].semitone);
      if (distance === 1 || distance === 2) {
        pairs.push({ from: sequence[i - 1].note, to: sequence[i].note, half: distance === 1 });
      }
    }
  }
  return pairs;
}

// ----- geometry, in the SVG's own user space -----

function svgRect(svg, element) {
  const r = element.getBoundingClientRect();
  if (!r.width && !r.height) return null;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const inverse = ctm.inverse();
  const toSvg = (x, y) => {
    const point = svg.createSVGPoint();
    point.x = x;
    point.y = y;
    return point.matrixTransform(inverse);
  };
  const tl = toSvg(r.left, r.top);
  const br = toSvg(r.right, r.bottom);
  return { left: tl.x, top: tl.y, right: br.x, bottom: br.y, width: br.x - tl.x, height: br.y - tl.y, cx: (tl.x + br.x) / 2, cy: (tl.y + br.y) / 2 };
}

// The notehead we are talking about plus the whole note group (stem, flags)
function notePlacement(note, voice) {
  const group = note.getSVGGElement?.();
  const svg = group?.ownerSVGElement;
  if (!svg) return null;
  const whole = svgRect(svg, group);
  if (!whole) return null;
  let head = null;
  group.querySelectorAll('.vf-notehead').forEach((element) => {
    const rect = svgRect(svg, element);
    if (!rect) return;
    if (!head || (voice === 'bottom' ? rect.bottom > head.bottom : rect.top < head.top)) head = rect;
  });
  return { svg, head: head || whole, whole };
}

function pillAt(cx, cy, width, height, kind) {
  const g = document.createElementNS(SVG_NS, 'g');
  const rect = document.createElementNS(SVG_NS, 'rect');
  rect.setAttribute('x', String(cx - width / 2));
  rect.setAttribute('y', String(cy - height / 2));
  rect.setAttribute('width', String(width));
  rect.setAttribute('height', String(height));
  rect.setAttribute('rx', String(height / 2));
  rect.setAttribute('fill', '#ffffff');
  rect.setAttribute('fill-opacity', '0.92');
  rect.setAttribute('stroke', kind.color);
  rect.setAttribute('stroke-width', String(Math.max(1, height * 0.09)));
  const text = document.createElementNS(SVG_NS, 'text');
  text.setAttribute('x', String(cx));
  text.setAttribute('y', String(cy));
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.setAttribute('font-family', 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif');
  text.setAttribute('font-weight', '700');
  text.setAttribute('font-size', String(height * 0.72));
  text.setAttribute('fill', kind.color);
  text.textContent = kind.text;
  const title = document.createElementNS(SVG_NS, 'title');
  title.textContent = kind.label;
  g.appendChild(title);
  g.appendChild(rect);
  g.appendChild(text);
  return g;
}

function scoreContainer(osmd) {
  return osmd?.container || osmd?.Drawer?.Backends?.[0]?.getSvgElement?.()?.parentNode || null;
}

export function clearStepHints(osmd) {
  const container = scoreContainer(osmd);
  if (!container) return;
  container.querySelectorAll(`g.${GROUP_CLASS}`).forEach((group) => group.remove());
}

/**
 * Draw the hints for one instrument: between the two noteheads, at the
 * height between them; at a line break, just left of the first note of the
 * new line.
 */
export function drawStepHints(osmd, { instrumentId, voice = 'top' }) {
  clearStepHints(osmd);
  if (!osmd || instrumentId === null || instrumentId === undefined) return;
  const groups = new Map(); // svg element -> our <g>
  for (const pair of collectStepPairs(osmd, instrumentId, voice)) {
    const a = notePlacement(pair.from, voice);
    const b = notePlacement(pair.to, voice);
    if (!a || !b || a.svg !== b.svg) continue;
    const space = Math.max(4, a.head.height, b.head.height);
    const height = Math.max(MIN_PILL_PX, space * 1.35);
    let width = height * 1.25;
    let cx;
    let cy;
    if (b.head.left < a.head.left) {
      // New system: just left of the first note of the line, at its height
      cx = b.head.left - width / 2 - 2;
      cy = b.head.cy;
    } else {
      // Between the noteheads, at the height between them; narrow the pill
      // a little when the notes are close rather than moving it away
      const gap = b.head.left - a.head.right;
      width = Math.min(width, Math.max(height * 0.95, gap - 2));
      cx = (a.head.right + b.head.left) / 2;
      cy = (a.head.cy + b.head.cy) / 2;
    }
    if (!groups.has(a.svg)) {
      const group = document.createElementNS(SVG_NS, 'g');
      group.setAttribute('class', GROUP_CLASS);
      group.setAttribute('pointer-events', 'none');
      groups.set(a.svg, group);
    }
    groups.get(a.svg).appendChild(pillAt(cx, cy, width, height, pair.half ? HALF : WHOLE));
  }
  // Always leave a (possibly empty) group behind so the observer can tell
  // "drawn" from "wiped by a re-render"
  const svgs = groups.size ? [...groups.keys()] : [...(scoreContainer(osmd)?.querySelectorAll('svg') || [])];
  svgs.forEach((svg) => {
    const group = groups.get(svg) || (() => {
      const g = document.createElementNS(SVG_NS, 'g');
      g.setAttribute('class', GROUP_CLASS);
      return g;
    })();
    svg.appendChild(group);
  });
}

/**
 * Draw now and keep the hints alive across OSMD re-renders. Returns a stop
 * function that also removes the hints.
 */
export function watchStepHints(osmd, options) {
  const container = scoreContainer(osmd);
  let frame = 0;
  let stopped = false;
  const redraw = () => {
    frame = 0;
    if (!stopped) drawStepHints(osmd, options);
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(redraw);
  };
  drawStepHints(osmd, options);
  let observer = null;
  if (container && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(() => {
      if (!container.querySelector(`g.${GROUP_CLASS}`)) schedule();
    });
    observer.observe(container, { childList: true, subtree: true });
  }
  return () => {
    stopped = true;
    if (observer) observer.disconnect();
    if (frame) cancelAnimationFrame(frame);
    clearStepHints(osmd);
  };
}
